import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthProvider";
import { useTheme } from "../../../context/ThemeProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { BASE_URL } from "../../../apiConfig";
import { endpoints } from "./endpoints";
import { toast } from "sonner";
import {
  KeyRound,
  Copy,
  RefreshCw,
  Trash2,
  Plus,
  Terminal,
  ShieldCheck,
  Lock,
  Check,
  Code2,
  Server,
  AlertTriangle,
  BookOpen,
  FileCode2,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Info,
} from "lucide-react";

const METHOD_STYLES = {
  GET: { bg: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400", ring: "ring-blue-200" },
  POST: { bg: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400", ring: "ring-emerald-200" },
  PUT: { bg: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400", ring: "ring-amber-200" },
  DELETE: { bg: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400", ring: "ring-red-200" },
};

const STATUS_STYLES = {
  200: { label: "200 OK", cls: "text-emerald-600 dark:text-emerald-400" },
  201: { label: "201 Created", cls: "text-emerald-600 dark:text-emerald-400" },
  400: { label: "400 Bad Request", cls: "text-amber-600 dark:text-amber-400" },
  401: { label: "401 Unauthorized", cls: "text-red-600 dark:text-red-400" },
  404: { label: "404 Not Found", cls: "text-red-600 dark:text-red-400" },
  422: { label: "422 Unprocessable", cls: "text-amber-600 dark:text-amber-400" },
  500: { label: "500 Server Error", cls: "text-red-600 dark:text-red-400" },
};

export default function ApiDocsPage() {
  const { token, user } = useAuth();
  const { isDark } = useTheme();
  const { t } = useLanguage();

  const [activeTab, setActiveTab] = useState("documentation");
  const [keys, setKeys] = useState([]);
  const [keyName, setKeyName] = useState("");
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [justCopied, setJustCopied] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [docs, setDocs] = useState({ base_url: BASE_URL + "/api", endpoints: endpoints });
  const [loadingDocs, setLoadingDocs] = useState(true);

  const baseUrl = `${BASE_URL}/api/v1`;

  const fetchKeys = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/apis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setKeys(data.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BASE_URL}/api/apis/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: keyName.trim() || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setLastGenerated(data.data);
        setKeyName("");
        toast.success(t("api_docs.key_generated"));
      } else {
        toast.error(data.message || t("api_docs.error"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("api_docs.error"));
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id) => {
    setRevokingId(id);
    try {
      const res = await fetch(`${BASE_URL}/api/apis/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t("api_docs.key_revoked"));
        fetchKeys();
        if (lastGenerated && lastGenerated.id === id) setLastGenerated(null);
      } else {
        toast.error(data.message || t("api_docs.error"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("api_docs.error"));
    } finally {
      setRevokingId(null);
    }
  };

  const copyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setJustCopied(id);
      toast.success(t("api_docs.copied"));
      setTimeout(() => setJustCopied(null), 1500);
    } catch (e) {
      toast.error(t("api_docs.copy_failed"));
    }
  };

  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookStatus, setWebhookStatus] = useState("false");
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [checkingWebhook, setCheckingWebhook] = useState(false);
  const [webhookKey, setWebhookKey] = useState("");

  const loadWebhook = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/apis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const active = data.data?.find((k) => k.url) || data.data?.[0];
        if (active) {
          setWebhookUrl(active.url || "");
          setWebhookStatus(active.status || "false");
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadWebhook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    try {
      const res = await fetch(`${BASE_URL}/api/apis/webhook-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ url: webhookUrl.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setWebhookStatus(data.data.status);
        setWebhookKey(data.data.api_key);
        toast.success(t("api_docs.webhook_save_success"));
        fetchKeys();
      } else {
        setWebhookStatus("false");
        toast.error(data.message || t("api_docs.webhook_save_error"));
      }
    } catch (e) {
      console.error(e);
      setWebhookStatus("false");
      toast.error(t("api_docs.webhook_save_error"));
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleToggleWebhook = async () => {
    setCheckingWebhook(true);
    const newStatus = webhookStatus === "true" ? "false" : "true";
    try {
      const res = await fetch(`${BASE_URL}/api/apis/webhook-url/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus === "true" }),
      });
      const data = await res.json();
      if (data.success) {
        setWebhookStatus(data.data.status || "false");
        toast.success(
          data.data.status === "true"
            ? t("api_docs.webhook_status_true")
            : t("api_docs.webhook_status_false")
        );
      } else {
        toast.error(data.message || t("api_docs.webhook_save_error"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("api_docs.webhook_save_error"));
    } finally {
      setCheckingWebhook(false);
    }
  };

  const tabs = useMemo(
    () => [
      { id: "documentation", label: t("api_docs.tab_documentation"), icon: BookOpen },
      { id: "keys", label: t("api_docs.tab_keys"), icon: KeyRound },
      { id: "webhook", label: t("api_docs.tab_webhook"), icon: Server },
    ],
    [t]
  );

  const activeKeyCount = keys.length;
  const latestKeyDate = keys[0]?.created_at;

  const curlExample = `curl -X GET "${baseUrl}/orders?status=nouveau&per_page=15" \\
  -H "X-API-Key: ${lastGenerated?.api_key || "VOTRE_CLE_API"}"`;

  const jsExample = `fetch("${baseUrl}/orders", {
  headers: {
    "X-API-Key": "${lastGenerated?.api_key || "VOTRE_CLE_API"}"
  }
})
  .then(res => res.json())
  .then(data => console.log(data));`;

  const createOrderExample = `curl -X POST "${baseUrl}/orders" \\
  -H "X-API-Key: ${lastGenerated?.api_key || "VOTRE_CLE_API"}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Ahmed",
    "phone": "0612345678",
    "city": "Casablanca",
    "address": "Rue 1, Maârif",
    "comment": "Commande API",
    "items": [
      { "sku": "NEXT-5", "quantity": 2 },
      { "sku": "VAR-5-2", "quantity": 1 }
    ]
  }'`;

  const formatDateTime = (dateString) => {
  const date = new Date(dateString);

  const pad = (num) => String(num).padStart(2, '0');

  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

  return (
    <div className="w-full">
      {/* ============ HERO ============ */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary-700)] via-[var(--color-primary-800)] to-[var(--color-primary-900)] dark:from-[var(--color-primary-100)] dark:via-[var(--color-primary-50)] dark:to-[var(--color-primary-50)]" />
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[var(--color-primary-200)]/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-[var(--color-primary-400)]/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "26px 26px",
          }}
        />

        <div className="relative z-10 px-6 py-10 lg:px-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold bg-white/10 border border-white/10 text-white">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.3)]" />
              {t("api_docs.hero_badge")}
            </span>
          </div>

          <h1 className="text-3xl lg:text-4xl font-extrabold text-white mb-3 tracking-tight">
            {t("api_docs.hero_title")}
          </h1>
          <p className="text-sm text-white/70 max-w-2xl leading-7 mb-6">
            {t("api_docs.hero_subtitle")}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/10 px-4 py-2 text-xs text-white">
              <Server className="w-4 h-4 text-[var(--color-primary-400)]" />
              <span className="text-white/60 font-medium mr-1">{t("api_docs.base_url")}:</span>
              <span className="font-mono text-white break-all">{baseUrl}</span>
              <button
                onClick={() => copyText(baseUrl, "base-url")}
                className="ml-1 p-1 rounded-md hover:bg-white/10 transition cursor-pointer"
                title={t("api_docs.copy")}
              >
                {justCopied === "base-url" ? (
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-white/70" />
                )}
              </button>
            </div>

            <div className="inline-flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-xs text-white/70">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              {t("api_docs.secured_label")}
            </div>
          </div>
        </div>
      </div>

      {/* ============ TABS ============ */}
      <div className="px-6 lg:px-10 border-b border-gray-200 dark:border-stone-800">
        <div className="flex gap-1 -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-semibold transition cursor-pointer border-b-2 ${
                activeTab === tab.id
                  ? "border-[var(--color-primary-500)] text-[var(--color-primary-500)]"
                  : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ============ CONTENT ============ */}
      <div className="p-6 lg:p-10 space-y-6">
        {activeTab === "webhook" ? (
          /* ------------------- WEBHOOK TAB ------------------- */
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="rounded-2xl border border-gray-200 dark:border-stone-800 bg-white dark:bg-stone-800 shadow-sm overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-100 dark:border-stone-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                    <Server className="w-5 h-5 text-[var(--color-primary-500)]" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                      {t("api_docs.webhook_title")}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t("api_docs.webhook_subtitle")}
                    </p>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold ${
                    webhookStatus === "true"
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                      : "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      webhookStatus === "true" ? "bg-emerald-500" : "bg-red-500"
                    }`}
                  />
                  {webhookStatus === "true"
                    ? t("api_docs.webhook_status_true")
                    : t("api_docs.webhook_status_false")}
                </span>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                    {t("api_docs.webhook_url_label")}
                  </label>
                  <div className="flex flex-col md:flex-row gap-3">
                    <input
                      type="url"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder={t("api_docs.webhook_url_placeholder")}
                      className="flex-1 outline-none px-3.5 py-2.5 text-xs border border-gray-300 dark:border-stone-700 dark:bg-stone-800 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-[var(--color-primary-500)] focus:border-transparent"
                    />
                    <button
                      onClick={handleSaveWebhook}
                      disabled={savingWebhook || !webhookUrl.trim()}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-xs font-semibold text-white bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] rounded-lg transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {savingWebhook ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                      {savingWebhook ? t("api_docs.webhook_saving") : t("api_docs.webhook_save_only")}
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={webhookStatus === "true"}
                        onChange={handleToggleWebhook}
                        disabled={checkingWebhook || !webhookUrl.trim()}
                        className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-blue-300 h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full before:bg-blue-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:ml-0.5"
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-300">
                        {t("api_docs.webhook_check_label")}
                      </span>
                    </label>
                    {checkingWebhook && <RefreshCw className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                  </div>
                  <p
                    className={`mt-2 text-[11px] flex items-center gap-1.5 ${
                      webhookStatus === "true"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-amber-600 dark:text-amber-400"
                    }`}
                  >
                    {webhookStatus === "true" ? (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5" />
                    )}
                    {webhookStatus === "true"
                      ? t("api_docs.webhook_active_msg")
                      : t("api_docs.webhook_inactive_msg")}
                  </p>

                  {webhookKey && (
                    <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-4 mt-4">
                      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-xs font-semibold mb-2">
                        <KeyRound className="w-4 h-4" />
                        {t("api_docs.webhook_api_key_title")}
                      </div>
                      <p className="text-[11px] text-emerald-600/80 dark:text-emerald-500/80 mb-2">
                        {t("api_docs.webhook_api_key_note")}
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white dark:bg-stone-900 border border-emerald-200 dark:border-emerald-500/30 rounded-lg px-3 py-2.5 text-xs font-mono break-all text-gray-800 dark:text-gray-100">
                          {webhookKey}
                        </code>
                        <button
                          onClick={() => copyText(webhookKey, "webhook-key")}
                          className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition cursor-pointer"
                        >
                          {justCopied === "webhook-key" ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

              {/* Response example */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-md bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                  <Terminal className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                </span>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.response_title")}
                </h2>
              </div>

              <div className="rounded-xl border border-gray-200 dark:border-stone-800 bg-stone-900 dark:bg-stone-800 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 dark:bg-stone-800  bg-stone-800 border-b border-stone-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="ml-2 text-[10px] font-mono text-stone-400">JSON</span>
                  <span className="ml-auto text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                    200 OK
                  </span>
                </div>
                <pre className="p-4 text-[11px] leading-6 text-amber-200 overflow-x-auto whitespace-pre">
{`{
    "order_code" : "NEXT-0000",
    "old_status" : "confirme",
    "new_status" : "Ramassé",
    "updated_at" : "2026-01-01 00:00:00" 
}`}
                </pre>
              </div>
            </section>

            <div className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-stone-700 p-4">
              <Info className="w-5 h-5 text-[var(--color-primary-500)] shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-xs font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.webhook_how_title")}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-6">
                  {t("api_docs.webhook_how_desc")}
                  {" "}
                  {t("api_docs.webhook_how_example")}
                </p>
                <div className="pt-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                    {t("api_docs.webhook_fields_title")}
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-1.5 text-[11px] text-gray-600 dark:text-gray-400">
                    <li className="flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-500" />
                      {t("api_docs.webhook_field_order_id")}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-500" />
                      {t("api_docs.webhook_field_status")}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-500" />
                      {t("api_docs.webhook_field_meta")}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-500" />
                      {t("api_docs.webhook_field_other")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === "keys" ? (
          /* ------------------- API KEYS TAB ------------------- */
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Generate card */}
            <div className="rounded-2xl border border-gray-200 dark:border-stone-800 bg-white dark:bg-stone-800 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-stone-700">
                <div className="w-10 h-10 rounded-xl bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-[var(--color-primary-500)]" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    {t("api_docs.generate_title")}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t("api_docs.generate_subtitle")}
                  </p>
                </div>
              </div>

              <div className="px-6 py-5">
                {lastGenerated ? (
                  <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-4">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-xs font-semibold mb-3">
                      <CheckCircle2 className="w-4 h-4" />
                      {t("api_docs.key_generated")}
                    </div>
                    <p className="text-[11px] text-emerald-600/80 dark:text-emerald-500/80 mb-2">
                      {t("api_docs.key_warning")}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white dark:bg-stone-900 border border-emerald-200 dark:border-emerald-500/30 rounded-lg px-3 py-2.5 text-xs font-mono break-all text-gray-800 dark:text-gray-100">
                        {lastGenerated.api_key}
                      </code>
                      <button
                        onClick={() => copyText(lastGenerated.api_key, "generated")}
                        className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition cursor-pointer"
                      >
                        {justCopied === "generated" ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                        {justCopied === "generated" ? t("api_docs.copied") : t("api_docs.copy")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid md:grid-cols-[1fr_auto] gap-3">
                      <input
                        type="text"
                        value={keyName}
                        onChange={(e) => setKeyName(e.target.value)}
                        placeholder={t("api_docs.key_name_placeholder")}
                        className="w-full outline-none px-3.5 py-2.5 text-xs border border-gray-300 dark:border-stone-700 dark:bg-stone-800 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-[var(--color-primary-500)] focus:border-transparent"
                      />
                      <button
                        onClick={handleGenerate}
                        disabled={generating}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-xs font-semibold text-white bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] rounded-lg transition cursor-pointer disabled:opacity-60"
                      >
                        {generating ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        {generating ? t("api_docs.generating") : t("api_docs.generate_btn")}
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 flex items-start gap-1.5">
                      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      {t("api_docs.generate_hint")}
                    </p>
                  </div>
                )}

                <div className="mt-4 flex items-center gap-5 text-[11px] text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                    <b className="text-gray-700 dark:text-gray-200">{activeKeyCount}</b>
                    {t("api_docs.active_keys")}
                  </span>
                  {latestKeyDate && (
                    <span className="inline-flex items-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5 text-gray-400" />
                      {t("api_docs.last_created")}:{" "}
                      {formatDateTime(latestKeyDate)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Keys list */}
            <div className="rounded-2xl border border-gray-200 dark:border-stone-700 bg-white dark:bg-stone-800 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-stone-700">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.manage_keys")}
                </h3>
              </div>

              {loadingKeys ? (
                <div className="p-8 space-y-3">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="h-16 bg-gray-100 dark:bg-stone-800 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : keys.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="mx-auto w-14 h-14 rounded-2xl bg-gray-100 dark:bg-stone-800 flex items-center justify-center mb-3">
                    <KeyRound className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {t("api_docs.no_keys")}
                  </p>
                  <p className="text-xs text-gray-400">{t("api_docs.no_keys_desc")}</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-stone-800">
                  {keys.map((k) => (
                    <li
                      key={k.id}
                      className="flex flex-wrap items-center gap-3 px-6 py-4"
                    >
                      <div className="w-9 h-9 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
                        <KeyRound className="w-4 h-4 text-stone-500 dark:text-stone-400" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                          {k.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-[11px] font-mono text-gray-500 dark:text-gray-400">
                            {k.key_preview}
                          </code>
                          <button
                            onClick={() => copyText(k.key_preview, `preview-${k.id}`)}
                            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-stone-800 transition cursor-pointer"
                            title={t("api_docs.copy")}
                          >
                            {justCopied === `preview-${k.id}` ? (
                              <Check className="w-3.5 h-3.5 text-emerald-500" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 text-gray-400" />
                            )}
                          </button>
                          <span className="text-[10px] text-gray-400">
                            {t("api_docs.created")}:{" "}
                            {formatDateTime(k.created_at)}
                          </span>
                          {k.last_used_at && (
                            <span className="text-[10px] text-emerald-500">
                              • {t("api_docs.last_used")}:{" "}
                              {formatDateTime(k.last_used_at)}
                            </span>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          if (window.confirm(t("api_docs.revoke_confirm"))) handleRevoke(k.id);
                        }}
                        disabled={revokingId === k.id}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 rounded-lg transition cursor-pointer disabled:opacity-60"
                      >
                        {revokingId === k.id ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                        {t("api_docs.revoke")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* How to use security note */}
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 p-4">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-800 dark:text-amber-400 mb-1">
                  {t("api_docs.security_title")}
                </p>
                <p className="text-xs text-amber-700/80 dark:text-amber-500/80 leading-6">
                  {t("api_docs.security_desc")}
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* ------------------- DOCUMENTATION TAB ------------------- */
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Introduction */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 rounded-md bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                  <BookOpen className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                </span>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.overview_title")}
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-7">
                {t("api_docs.overview_desc")}
              </p>
            </section>

            {/* Authentication */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 rounded-md bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                  <Lock className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                </span>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.auth_title")}
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-7 mb-4">
                {t("api_docs.auth_desc")}
              </p>

              <div className="rounded-xl border border-gray-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-800 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-stone-100 dark:bg-stone-800 border-b border-gray-200 dark:border-stone-700">
                  <Terminal className="w-3.5 h-3.5 text-stone-400" />
                  <span className="text-[11px] font-mono text-stone-500">
                    {t("api_docs.header_example")}
                  </span>
                </div>
                <div className="p-4 bg-white dark:bg-stone-800">
                  <pre className="text-xs leading-6 text-stone-700 dark:text-stone-300 overflow-x-auto whitespace-pre">
{`GET /v1/orders
Host: ${baseUrl}
X-API-Key: ${lastGenerated?.api_key || "xxxxxxx"}`}
                  </pre>
                </div>
              </div>
            </section>

            {/* Endpoints */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-md bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                  <Code2 className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                </span>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.endpoints_title")}
                </h2>
              </div>

              <div className="space-y-4">
                {(loadingDocs ? docs.endpoints : docs.endpoints).length === 0 && loadingDocs ? (
                  <div className="h-20 bg-gray-100 dark:bg-stone-800 rounded-xl animate-pulse" />
                ) : (
                  [
                    {
                      method: "GET",
                      path: "/api/v1/cities",
                      summary: t("api_docs.endpoint_cities"),
                      params: [],
                      responses: [200, 401],
                    },
                    {
                      method: "GET",
                      path: "/api/v1/products",
                      summary: t("api_docs.endpoint_products"),
                      params: [
                        { name: "category_id", type: "integer", optional: true, desc: t("api_docs.filter_category") },
                        { name: "search", type: "string", optional: true, desc: t("api_docs.filter_search") },
                        { name: "per_page", type: "integer", optional: true, desc: t("api_docs.per_page") },
                      ],
                      responses: [200, 401],
                    },
                    {
                      method: "GET",
                      path: "/api/v1/orders",
                      summary: t("api_docs.endpoint_orders"),
                      params: [
                        { name: "status", type: "string", optional: true, desc: t("api_docs.filters") },
                        { name: "per_page", type: "integer", optional: true, desc: t("api_docs.per_page") },
                      ],
                      responses: [200, 401],
                    },
                    {
                      method: "POST",
                      path: "/api/v1/orders",
                      summary: t("api_docs.endpoint_create_order"),
                      params: [
                        { name: "name", type: "string", optional: false, desc: t("api_docs.order_name") },
                        { name: "phone", type: "string", optional: false, desc: t("api_docs.order_phone") },
                        { name: "city", type: "string", optional: false, desc: t("api_docs.order_city") },
                        { name: "address", type: "string", optional: false, desc: t("api_docs.order_address") },
                        { name: "comment", type: "string", optional: true, desc: t("api_docs.order_comment") },
                        { name: "items[][sku]", type: "string", optional: false, desc: t("api_docs.order_sku") },
                        { name: "items[][quantity]", type: "integer", optional: false, desc: t("api_docs.order_quantity") },
                      ],
                      responses: [201, 401, 422],
                    },
                  ].map((ep, idx) => {
                    const m = METHOD_STYLES[ep.method] || METHOD_STYLES.GET;
                    return (
                      <div
                        key={idx}
                        className="rounded-xl border border-gray-200 dark:border-stone-800 bg-white dark:bg-stone-800 shadow-sm overflow-hidden"
                      >
                        <div className="flex items-center justify-between flex-wrap gap-3 px-5 py-4 border-b bg-stone-100 dark:bg-stone-800 border-gray-100 dark:border-stone-700">
                          <div className="flex items-center gap-3">
                            <span
                              className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-bold ring-1 ${m.bg} ${m.ring}`}
                            >
                              {ep.method}
                            </span>
                            <code className="text-xs font-mono font-medium text-gray-800 dark:text-gray-100 break-all">
                              {baseUrl.replace("/api/v1", "")}{ep.path}
                            </code>
                          </div>
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-1 text-[10px] font-semibold">
                            <Lock className="w-3 h-3" />
                            {t("api_docs.secured")}
                          </span>
                        </div>

                        <div className="px-5 py-4">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                            {ep.summary}
                          </p>

                          {/* Parameters */}
                          <div className="mb-4">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                              {t("api_docs.params_title")}
                            </p>
                            <div className="rounded-lg border border-gray-100 dark:border-stone-800 divide-y divide-gray-100 dark:divide-stone-800">
                              {(ep.params || []).map((p, i) => (
                                <div
                                  key={i}
                                  className="flex items-center gap-3 px-4 py-2.5 text-xs"
                                >
                                  <code className="font-mono text-gray-800 dark:text-gray-100 w-24 shrink-0">
                                    {p.name}
                                  </code>
                                  <span className="text-[10px] font-mono text-gray-400 w-14 shrink-0">
                                    {p.type}
                                  </span>
                                  <span
                                    className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                                      p.optional
                                        ? "bg-gray-100 dark:bg-stone-800 text-gray-500"
                                        : "bg-red-50 dark:bg-red-500/10 text-red-500"
                                    }`}
                                  >
                                    {p.optional ? t("api_docs.optional") : t("api_docs.required")}
                                  </span>
                                  <span className="text-gray-500 dark:text-gray-400 flex-1 min-w-0">
                                    {p.desc}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {ep.method === "POST" && ep.path === "/api/v1/orders" && (
                              <p className="mt-2 text-xs text-red-500 dark:text-red-400">
                                {t("api_docs.order_sku_note")}
                              </p>
                            )}
                          </div>

                          {/* Response codes */}
                          <div>
                            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                              {t("api_docs.responses_title")}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {ep.responses.map((code) => {
                                const s = STATUS_STYLES[code];
                                const ok = code < 400;
                                return (
                                  <span
                                    key={code}
                                    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[10px] font-semibold ${s.cls} bg-stone-50 dark:bg-stone-800 border border-gray-100 dark:border-stone-700`}
                                  >
                                    {ok ? (
                                      <CheckCircle2 className="w-3 h-3" />
                                    ) : (
                                      <XCircle className="w-3 h-3" />
                                    )}
                                    {s.label}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            {/* Code examples */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-md bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                  <FileCode2 className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                </span>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.examples_title")}
                </h2>
              </div>

              <div className="space-y-4">
                {/* cURL */}
                <div className="rounded-xl border border-gray-200 dark:bg-stone-800 dark:border-stone-800 bg-stone-900 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 dark:bg-stone-800 bg-stone-800 border-b border-stone-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="ml-2 text-[10px] font-mono text-stone-400">curl</span>
                    <button
                      onClick={() => copyText(curlExample, "curl")}
                      className="ml-auto p-1 rounded hover:bg-stone-800 transition cursor-pointer"
                    >
                      {justCopied === "curl" ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-stone-400" />
                      )}
                    </button>
                  </div>
                  <pre className="p-4 text-[11px] leading-6 text-emerald-300 overflow-x-auto whitespace-pre">
{curlExample}
                  </pre>
                </div>

                {/* JavaScript */}
                <div className="rounded-xl border border-gray-200 dark:bg-stone-800 dark:border-stone-800 bg-stone-900 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 dark:bg-stone-800 bg-stone-800 border-b border-stone-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="ml-2 text-[10px] font-mono text-stone-400">JavaScript</span>
                    <button
                      onClick={() => copyText(jsExample, "js")}
                      className="ml-auto p-1 rounded hover:bg-stone-800 transition cursor-pointer"
                    >
                      {justCopied === "js" ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-stone-400" />
                      )}
                    </button>
                  </div>
                  <pre className="p-4 text-[11px] leading-6 text-sky-300 overflow-x-auto whitespace-pre">
{jsExample}
                  </pre>
                </div>

                {/* Create order */}
                <div className="md:col-span-2 rounded-xl border dark:bg-stone-800 border-gray-200 dark:border-stone-800 bg-stone-900 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 dark:bg-stone-800 bg-stone-800 border-b border-stone-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="ml-2 text-[10px] font-mono text-stone-400">curl -X POST</span>
                    <button
                      onClick={() => copyText(createOrderExample, "create-order")}
                      className="ml-auto p-1 rounded hover:bg-stone-800 transition cursor-pointer"
                    >
                      {justCopied === "create-order" ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-stone-400" />
                      )}
                    </button>
                  </div>
                  <pre className="p-4 text-[11px] leading-6 text-violet-300 overflow-x-auto whitespace-pre">
{createOrderExample}
                  </pre>
                </div>
              </div>
            </section>

            {/* Response example */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-md bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                  <Terminal className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                </span>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.response_title")}
                </h2>
              </div>

              <div className="rounded-xl border border-gray-200 dark:border-stone-800 bg-stone-900 dark:bg-stone-800 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 dark:bg-stone-800  bg-stone-800 border-b border-stone-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="ml-2 text-[10px] font-mono text-stone-400">JSON</span>
                  <span className="ml-auto text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                    200 OK
                  </span>
                </div>
                <pre className="p-4 text-[11px] leading-6 text-amber-200 overflow-x-auto whitespace-pre">
{`{
  "success": true,
  "data": {
    "current_page": 1,
    "data": [
      {
        "id": 1024,
        "code": "NEXT-1024",
        "status": "nouveau",
        "qty": 2,
        "total": 450.00,
        "client": {
          "name": "Ahmed",
          "phone": 661234567,
          "city": "Casablanca",
          "address": "Rue 1, Maârif"
        },
        "created_at": "2026-08-27T10:20:00.000000Z"
      }
    ],
    "per_page": 15,
    "total": 1
  }
}`}
                </pre>
              </div>
            </section>

            {/* Error codes */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-md bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 flex items-center justify-center">
                  <XCircle className="w-3.5 h-3.5 text-[var(--color-primary-500)]" />
                </span>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {t("api_docs.errors_title")}
                </h2>
              </div>

              <div className="rounded-xl bg-white dark:bg-stone-800 border border-gray-200 dark:border-stone-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-stone-800 text-left text-[10px] border-b border-gray-100 dark:border-stone-700 uppercase tracking-wider text-gray-400">
                      <th className="px-4 py-2.5 font-semibold">{t("api_docs.error_code")}</th>
                      <th className="px-4 py-2.5 font-semibold">{t("api_docs.error_message")}</th>
                      <th className="px-4 py-2.5 font-semibold">{t("api_docs.error_desc")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-stone-800">
                    <tr>
                      <td className="px-4 py-3 font-mono text-red-500 font-medium">401</td>
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-100">
                        {t("api_docs.err_401_msg")}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {t("api_docs.err_401_desc")}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-mono text-amber-500 font-medium">422</td>
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-100">
                        {t("api_docs.err_422_msg")}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {t("api_docs.err_422_desc")}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-mono text-red-500 font-medium">500</td>
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-100">
                        {t("api_docs.err_500_msg")}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {t("api_docs.err_500_desc")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Quick link to generate key */}
            <div className="flex items-center justify-between flex-wrap gap-3 rounded-xl bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-500)]/10 border border-[var(--color-primary-200)] dark:border-[var(--color-primary-500)]/30 p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white dark:bg-stone-900 flex items-center justify-center">
                  <KeyRound className="w-4 h-4 text-[var(--color-primary-500)]" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-900 dark:text-gray-100">
                    {t("api_docs.cta_title")}
                  </p>
                  <p className="text-[11px] text-gray-600 dark:text-gray-400">
                    {t("api_docs.cta_desc")}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveTab("keys")}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] rounded-lg transition cursor-pointer"
              >
                {t("api_docs.cta_btn")}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
